import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { OnboardingPhase, OnboardingTask, resolveVariant } from '../../interfaces/onboarding.interfaces';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingAnalyticsService } from '../../services/onboarding-analytics.service';
import { OnboardingModalService } from '../../services/onboarding-modal.service';
import { TourOrchestratorService } from '../../services/tour-orchestrator.service';

/**
 * Banner inteligente de onboarding del Home. Consume `OnboardingService.activePhase$` (vía async pipe,
 * sin suscripción manual) y renderiza la fase activa de forma 100% data-driven: header con anillo de
 * progreso, título/subtítulo, tiempo restante y CTA, más el grid de tareas. No ramifica por `phase_id`;
 * la variante de cada tarjeta se deriva con `resolveVariant`.
 *
 * El modal de celebración NO se hospeda aquí: vive en el shell (`AppComponent`) para dispararse en
 * cualquier ruta cuando una fase llega a 100% (vía `OnboardingService.celebration$`).
 */
@Component({
    selector: 'fina-onboarding-dashboard',
    templateUrl: './onboarding-dashboard.component.html',
    styleUrls: ['./onboarding-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingDashboardComponent implements OnInit, OnDestroy {
    /** Fase activa según el backend (`currentPhaseId` / `status`), derivada en el servicio. */
    readonly activePhase$ = this.onboarding.activePhase$;

    /** Reexpone el helper puro al template. */
    resolveVariant = resolveVariant;

    /** Estado de colapso del banner (solo aplica al layout horizontal / Fase 2). */
    collapsed = false;

    private readonly _bannerSub = new Subscription();

    constructor(
        private readonly onboarding: OnboardingService,
        private readonly router: Router,
        private readonly tourOrchestrator: TourOrchestratorService,
        private readonly onboardingModalService: OnboardingModalService,
        private readonly analytics: OnboardingAnalyticsService,
    ) {}

    ngOnInit(): void {
        // onboarding_banner_viewed: se dispara al renderizar una fase activa (proxy de "viewed" sin UI nueva).
        // distinctUntilChanged por phase_id → un evento por fase, no por cada emisión del estado.
        this._bannerSub.add(
            this.activePhase$
                .pipe(
                    filter((phase): phase is OnboardingPhase => !!phase),
                    // Banner retenido con el onboarding ya cerrado (FINA-3431): no es una vista de fase activa.
                    filter(() => !this.onboarding.currentState?.completion?.completed),
                    distinctUntilChanged((a, b) => a.phase_id === b.phase_id),
                )
                .subscribe(phase => this.analytics.bannerViewed(phase)),
        );
    }

    ngOnDestroy(): void {
        this._bannerSub.unsubscribe();
    }

    /**
     * Tareas VISIBLES en el banner. Capa de PRESENTACIÓN pura (Cero Cables): NO toca el modelo, el progreso ni
     * `tasks_done`/`tasks_total`. Mientras haya alguna tarea OBLIGATORIA pendiente, muestra SOLO las obligatorias;
     * cuando todas las obligatorias estén completas, revela TODAS las aplicables (obligatorias + opcionales).
     * Naturalmente no-op en fases sin mezcla: Fase 1 (todo obligatorio) y Fase 3 (todo opcional → no hay
     * obligatorias pendientes) devuelven siempre el arreglo completo. El modal de detalle NO usa esto (sigue
     * listando todo en sus pestañas Obligatorias/Opcionales).
     */
    visibleTasks(phase: OnboardingPhase): OnboardingTask[] {
        const hasPendingMandatory = phase.tasks.some(task => task.is_mandatory && !task.isCompleted);
        return hasPendingMandatory ? phase.tasks.filter(task => task.is_mandatory) : phase.tasks;
    }

    /**
     * Índice de la primera tarea no completada DENTRO de la lista visible => "Siguiente paso". Recibe el arreglo
     * VISIBLE (no `phase.tasks`) para que la variante "next" se pinte en la tarjeta correcta tras el filtro.
     */
    nextIndex(tasks: OnboardingTask[]): number {
        return tasks.findIndex(task => !task.isCompleted);
    }

    toggleCollapsed(): void {
        this.collapsed = !this.collapsed;
    }

    /** Abre el modal de detalle (checklist Obligatorias/Opcionales) de la fase activa. */
    openPhaseDetails(): void {
        this.analytics.checklistOpened('banner_button');
        this.onboardingModalService.openPhaseDetail();
    }

    onTaskClick(task: OnboardingTask): void {
        if (!task?.link) return;
        this.analytics.stepClicked(task);
        // Navega siempre; pero solo solicita el auto-trigger del tour si la tarea NO está completada.
        // (El orquestador también lo re-verifica en startTourByTaskId — defensa en 2 capas.)
        if (!task.isCompleted) {
            this.tourOrchestrator.requestTourForTask(task.task_id, task.link);
        }
        this.router.navigateByUrl(task.link);
    }
}
